/* import __COLOCATED_TEMPLATE__ from './current-calculation.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { eng3, userEntryToNumber } from '@ebiz/pretty-num';
import type IntlService from 'ember-intl/services/intl';

interface CurrentCalculationArgs {
  title: string;
  close: () => void;
  selectedPart: string;
  selectedPartData: { sensitivity: string; area: string };
  sensorLimits: { max: number; min: number };
  assignPeakCurrent: (peakCurrent: string) => void;
}

export default class CurrentCalculation extends Component<CurrentCalculationArgs> {
  @service declare intl: IntlService;
  @tracked assignEnabled = false;
  @tracked invalidPeakLightIntensity = false;
  @tracked invalidPeakCurrent = false;
  @tracked invalidInput = false;
  @tracked invalidInputValue?: string;
  @tracked invalidInputField?: string;
  @tracked peakCurrent = '';
  @tracked peakLightIntensity = '';

  currentOfLightIntensity(intensity: number): number {
    return (
      intensity *
      (userEntryToNumber(this.args.selectedPartData.sensitivity, NaN) *
        (userEntryToNumber(this.args.selectedPartData.area, NaN) / 1e6))
    );
  }

  lightIntensityOfCurrent(current: number): number {
    return (
      current /
      (userEntryToNumber(this.args.selectedPartData.sensitivity, NaN) *
        (userEntryToNumber(this.args.selectedPartData.area, NaN) / 1e6))
    );
  }

  invalidUserInput(value: string, field: string): void {
    this.invalidInput = true;
    this.invalidInputValue = value;
    this.invalidInputField = field;
  }

  resetValidity(): void {
    this.assignEnabled = false;
    this.invalidPeakCurrent = false;
    this.invalidPeakLightIntensity = false;
    this.invalidInput = false;
  }

  enableAssignButton(): void {
    this.assignEnabled = true;
  }

  get lightIntensityLimits(): { min: number; max: number } {
    const maxValue = this.lightIntensityOfCurrent(this.args.sensorLimits.max);
    const minValue = this.lightIntensityOfCurrent(this.args.sensorLimits.min);
    return { min: minValue, max: maxValue };
  }

  @action applyAndClose(closeDialog: () => void) {
    this.args.assignPeakCurrent(this.peakCurrent);
    closeDialog();
  }

  @action calculatePeakLightIntensity(event: InputEvent): void {
    const target = event.target as HTMLInputElement;
    const peakLimits = this.args.sensorLimits;

    this.peakCurrent = target.value;

    const current = userEntryToNumber(this.peakCurrent, NaN);
    const intensity = this.lightIntensityOfCurrent(current);

    if (this.peakCurrent != '' && peakLimits) {
      if (!isNaN(current)) {
        if (current <= peakLimits.max && current >= peakLimits.min) {
          this.peakLightIntensity = eng3(intensity);
          this.resetValidity();
          this.enableAssignButton();
        } else {
          this.peakLightIntensity = '';
          this.resetValidity();
          this.invalidPeakCurrent = true;
        }
      } else {
        this.peakLightIntensity = '';
        this.resetValidity();
        this.invalidPeakCurrent = true;
        this.invalidUserInput(this.peakCurrent, this.intl.t('current-calculation.peak-current').replace(':', ''));
      }
    } else if (this.invalidPeakCurrent || this.peakCurrent === '') {
      this.peakLightIntensity = '';
      this.resetValidity();
    }
  }

  @action calculatePeakCurrent(event: InputEvent): void {
    const target = event.target as HTMLInputElement;
    const peakLimits = this.args.sensorLimits;

    this.peakLightIntensity = target.value;

    const intensity = userEntryToNumber(this.peakLightIntensity, NaN);
    const current = this.currentOfLightIntensity(intensity);

    if (this.peakLightIntensity != '' && peakLimits) {
      if (!isNaN(current)) {
        if (current <= peakLimits.max && current >= peakLimits.min) {
          this.peakCurrent = eng3(current);
          this.resetValidity();
          this.enableAssignButton();
        } else {
          this.peakCurrent = '';
          this.resetValidity();
          this.invalidPeakLightIntensity = true;
        }
      } else {
        this.peakCurrent = '';
        this.resetValidity();
        this.invalidPeakLightIntensity = true;
        this.invalidUserInput(
          this.peakLightIntensity,
          this.intl.t('current-calculation.peak-light-intensity').replace(':', ''),
        );
      }
    } else if (this.invalidPeakLightIntensity || this.peakLightIntensity === '') {
      this.peakCurrent = '';
      this.resetValidity();
    }
  }
}
